import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload, ImageIcon, UserPlus2, Search, FolderUp, Loader2, RectangleHorizontal, RectangleVertical, Ban, AlertTriangle, Coins, Layers, Pencil, Store, Star, Sparkles, RefreshCw, Printer, Check, X } from "lucide-react";
import { RarityPill } from "@/components/Atoms";
import { compressImage, dataUrlSizeKB } from "@/lib/image";
import { cardImageStyle, CardHoverPreview } from "@/components/Card3D";
import { PaginationControls } from "@/components/PaginationControls";
import { cn } from "@/lib/utils";
import PrintSheetDialog from "./PrintSheetDialog";
import { EditionCodesDialog } from "./EditionCodesDialog";
import { PurgeUnmintedDialog } from "./PurgeUnmintedDialog";
import { DedupeTemplatesDialog } from "./DedupeTemplatesDialog";
import { AutoOrientTemplatesButton } from "./AutoOrientTemplatesButton";

const RARITIES = ["common", "uncommon", "rare", "epic", "legendary", "goddess"];

function ImageUpload({ value, onChange, label, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) { toast.error("Image must be < 25MB before compression"); return; }
    setBusy(true);
    try {
      const compressed = await compressImage(f, { maxSize: 2400, quality: 0.92 });
      onChange(compressed);
      const kb = dataUrlSizeKB(compressed);
      toast.success(`Compressed: ${kb} KB`);
    } catch (err) {
      toast.error("Compression failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">{label}</Label>
      <div className="mt-1.5 flex items-center gap-3">
        <div className="w-20 h-28 rounded-lg border border-white/10 bg-[hsl(220_18%_12%)] flex items-center justify-center overflow-hidden">
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="h-5 w-5 text-white/30" />}
        </div>
        <div className="flex gap-2">
          <input ref={ref} data-testid={testid} type="file" accept="image/*" onChange={onFile} className="hidden" />
          <Button type="button" size="sm" variant="outline" onClick={() => ref.current.click()} disabled={busy} className="border-white/15 text-white">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Upload
          </Button>
          {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} className="text-white/60">Clear</Button>}
        </div>
      </div>
      {value && <div className="text-[10px] text-white/30 mt-1 font-mono">~{dataUrlSizeKB(value)} KB</div>}
    </div>
  );
}

function CardFramingEditor({ form, setForm }) {
  const isLandscape = form.orientation === "landscape";
  const previewCard = {
    front_image: form.front_image,
    image_fit: form.image_fit,
    image_scale: form.image_scale,
    image_pos_x: form.image_pos_x,
    image_pos_y: form.image_pos_y,
  };
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const OrientBtn = ({ value, Icon, label }) => (
    <button
      type="button"
      data-testid={`template-orientation-${value}`}
      onClick={() => set({ orientation: value })}
      className={cn(
        "flex-1 flex flex-col items-center gap-1 rounded-lg border px-3 py-2 transition-colors duration-150",
        form.orientation === value
          ? "border-cyan-400/70 bg-cyan-400/10 text-cyan-200"
          : "border-white/10 bg-[hsl(220_18%_12%)] text-white/60 hover:border-white/25"
      )}
    >
      <Icon className="h-5 w-5" />
      <span className="font-mono text-[10px] uppercase tracking-[0.2em]">{label}</span>
    </button>
  );
  return (
    <div className="sm:col-span-2 rounded-lg border border-white/10 bg-[hsl(220_18%_10%)] p-3">
      <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Card Layout & Framing</Label>
      <div className="mt-3 flex flex-col sm:flex-row gap-4">
        {/* Live preview */}
        <div className="flex items-center justify-center shrink-0">
          <div className={cn("relative rounded-xl overflow-hidden border border-white/15 bg-black shadow-lg", isLandscape ? "w-[224px] h-[160px]" : "w-[160px] h-[224px]")} data-testid="template-framing-preview">
            {form.front_image ? (
              <img src={form.front_image} alt="preview" className="absolute inset-0 w-full h-full" style={cardImageStyle(previewCard)} draggable={false} />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs text-center px-3">Upload a front image to frame it</div>
            )}
          </div>
        </div>
        {/* Controls */}
        <div className="flex-1 space-y-3">
          <div className="flex gap-2">
            <OrientBtn value="portrait" Icon={RectangleVertical} label="Portrait" />
            <OrientBtn value="landscape" Icon={RectangleHorizontal} label="Landscape" />
          </div>
          <div>
            <Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Image Fit</Label>
            <Select value={form.image_fit} onValueChange={(v) => set({ image_fit: v })}>
              <SelectTrigger data-testid="template-fit-select" className="mt-1 h-9 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                <SelectItem value="cover">Fill (cover)</SelectItem>
                <SelectItem value="contain">Fit (contain)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Zoom</Label><span className="font-mono text-[10px] text-white/40">{Number(form.image_scale).toFixed(2)}x</span></div>
            <Slider data-testid="template-scale-slider" value={[form.image_scale]} min={0.5} max={3} step={0.05} onValueChange={([v]) => set({ image_scale: v })} className="mt-2" />
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Horizontal</Label><span className="font-mono text-[10px] text-white/40">{Math.round(form.image_pos_x)}%</span></div>
            <Slider data-testid="template-posx-slider" value={[form.image_pos_x]} min={0} max={100} step={1} onValueChange={([v]) => set({ image_pos_x: v })} className="mt-2" />
          </div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-white/60 text-[10px] uppercase tracking-[0.2em]">Vertical</Label><span className="font-mono text-[10px] text-white/40">{Math.round(form.image_pos_y)}%</span></div>
            <Slider data-testid="template-posy-slider" value={[form.image_pos_y]} min={0} max={100} step={1} onValueChange={([v]) => set({ image_pos_y: v })} className="mt-2" />
          </div>
          <Button type="button" variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => set({ image_fit: "cover", image_scale: 1, image_pos_x: 50, image_pos_y: 50 })}>Reset framing</Button>
        </div>
      </div>
    </div>
  );
}

function MintDialog({ template }) {
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState("user");
  const [price, setPrice] = useState(template.list_price ?? 100);
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const mintToUser = async () => {
    try {
      await api.post("/admin/mint", { template_id: template.id, owner_email: email });
      toast.success("Card minted to user");
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      setOpen(false);
      setEmail("");
    } catch (e) { toast.error(apiError(e, "Mint failed")); }
  };
  const listInStore = async () => {
    try {
      await api.post("/admin/listings", { type: "template", ref_id: template.id, price_credits: Number(price) || 0, active: true });
      toast.success("Listed in store");
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      qc.invalidateQueries({ queryKey: ["listings"] });
      qc.invalidateQueries({ queryKey: ["admin-listings"] });
      setOpen(false);
    } catch (e) { toast.error(apiError(e, "Listing failed")); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title="Mint" className="h-7 w-7 p-0 text-white/60 hover:text-white" data-testid={`mint-button-${template.id}`}><UserPlus2 className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em]">MINT — {template.title}</DialogTitle>
        </DialogHeader>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 w-full">
            <TabsTrigger value="user" data-testid="mint-mode-user" className="flex-1 data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px]"><UserPlus2 className="h-3.5 w-3.5 mr-1" /> To User</TabsTrigger>
            <TabsTrigger value="store" data-testid="mint-mode-store" className="flex-1 data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px]"><Store className="h-3.5 w-3.5 mr-1" /> To Store</TabsTrigger>
          </TabsList>
        </Tabs>
        {mode === "user" ? (
          <div className="space-y-3">
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Owner Email</Label>
            <Input data-testid="mint-owner-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="collector@example.com" className="bg-[hsl(220_18%_12%)] border-white/10" />
            <Button data-testid="mint-confirm-button" onClick={mintToUser} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">Mint Card to User</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Store Price (credits)</Label>
            <Input data-testid="mint-store-price-input" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} className="bg-[hsl(220_18%_12%)] border-white/10" />
            <div className="text-[11px] text-white/40">Lists this card in the store. Each purchase mints a new edition (up to {template.edition_count} total) to the buyer.</div>
            <Button data-testid="mint-list-store-button" onClick={listInStore} className="w-full bg-cyan-400 text-black hover:bg-cyan-300"><Store className="h-4 w-4 mr-1.5" /> List in Store</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditTemplateDialog({ template }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();
  const init = () => setForm({
    title: template.title || "",
    description: template.description || "",
    rarity: template.rarity || "common",
    edition_count: template.edition_count || 1,
    base_value: template.base_value ?? "",
    front_image: template.front_image || null,
    back_image: template.back_image || null,
    lore: template.lore || "",
    tags: (template.tags || []).join(", "),
    orientation: template.orientation || "portrait",
    image_fit: template.image_fit || "cover",
    image_scale: template.image_scale ?? 1,
    image_pos_x: template.image_pos_x ?? 50,
    image_pos_y: template.image_pos_y ?? 50,
  });
  const save = async () => {
    try {
      setSaving(true);
      const payload = {
        ...form,
        edition_count: Number(form.edition_count) || 1,
        base_value: (form.base_value === "" || form.base_value == null) ? null : Number(form.base_value),
        tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.patch(`/admin/templates/${template.id}`, payload);
      toast.success("Card updated");
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      qc.invalidateQueries({ queryKey: ["home-featured"] });
      qc.invalidateQueries({ queryKey: ["template-editions", template.id] });
      setOpen(false);
    } catch (e) { toast.error(apiError(e, "Update failed")); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`edit-template-${template.id}`} className="h-7 w-7 p-0 text-white/60 hover:text-white" title="Edit card"><Pencil className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em]">EDIT CARD — {template.title}</DialogTitle>
        </DialogHeader>
        {form && (
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <div className="sm:col-span-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Title</Label>
              <Input data-testid="edit-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Description</Label>
              <Textarea data-testid="edit-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Rarity</Label>
              <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
                <SelectTrigger data-testid="edit-rarity-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                  {RARITIES.map((r) => <SelectItem key={r} value={r} className="uppercase tracking-[0.2em]">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Edition Count</Label>
              <Input data-testid="edit-edition-input" type="number" min={1} value={form.edition_count} onChange={(e) => setForm({ ...form, edition_count: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              <div className="text-[10px] text-white/40 mt-1">{template.minted_count || 0} already minted (can't go lower).</div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Base Value (credits)</Label>
              <Input data-testid="edit-basevalue-input" type="number" min={0} value={form.base_value} onChange={(e) => setForm({ ...form, base_value: e.target.value })} placeholder="Leave blank for rarity default" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              <div className="text-[10px] text-white/40 mt-1">Drives quick-sell value (players get 1/10 of this). Blank = rarity default.</div>
            </div>
            <ImageUpload value={form.front_image} onChange={(v) => setForm({ ...form, front_image: v })} label="Front Image" testid="edit-front-upload" />
            <ImageUpload value={form.back_image} onChange={(v) => setForm({ ...form, back_image: v })} label="Back Image (optional)" testid="edit-back-upload" />
            <CardFramingEditor form={form} setForm={setForm} />
            <div className="sm:col-span-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Lore (optional)</Label>
              <Textarea data-testid="edit-lore-input" value={form.lore} onChange={(e) => setForm({ ...form, lore: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Tags (comma-separated)</Label>
              <Input data-testid="edit-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="dragon, fire, mythical" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div className="sm:col-span-2 rounded-lg border border-white/10 bg-[hsl(220_18%_10%)] p-2 text-[11px] text-white/45">
              Editing the card updates its template, future mints, and store display. Already-minted editions keep their own art — edit those individually via <span className="text-white/70">Editions</span>.
            </div>
            <Button data-testid="edit-save-button" onClick={save} disabled={!form.title || !form.front_image || saving} className="sm:col-span-2 bg-cyan-400 text-black hover:bg-cyan-300">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function EditionsManagerDialog({ template }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // the slot being edited
  const [form, setForm] = useState(null);
  const qc = useQueryClient();

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["template-editions", template.id],
    queryFn: async () => (await api.get(`/admin/templates/${template.id}/editions`)).data,
    enabled: open,
  });
  const editions = data?.editions || [];

  // Resolve the image a slot should display (falls back to the template default).
  const slotImage = (s) => (s.uses_default_image ? template.front_image : s.front_image);

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      title: s.title || template.title || "",
      front_image: slotImage(s) || null,
      back_image: s.back_image || null,
      edition_date: s.edition_date || "",
      orientation: s.orientation || template.orientation || "portrait",
      image_fit: s.image_fit || template.image_fit || "cover",
      image_scale: s.image_scale ?? template.image_scale ?? 1,
      image_pos_x: s.image_pos_x ?? template.image_pos_x ?? 50,
      image_pos_y: s.image_pos_y ?? template.image_pos_y ?? 50,
    });
  };

  const save = async () => {
    try {
      const payload = { ...form, edition_date: form.edition_date || null };
      if (editing.status === "minted") {
        await api.patch(`/admin/cards/${editing.card_id}`, payload);
        toast.success(`Edition #${editing.edition_number} updated`);
      } else {
        await api.put(`/admin/templates/${template.id}/editions/${editing.edition_number}`, payload);
        toast.success(`Edition #${editing.edition_number} designed`);
      }
      setEditing(null); setForm(null);
      refetch();
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
    } catch (e) { toast.error(apiError(e, "Save failed")); }
  };

  const clearDesign = async () => {
    try {
      await api.delete(`/admin/templates/${template.id}/editions/${editing.edition_number}`);
      toast.success(`Edition #${editing.edition_number} reset to default`);
      setEditing(null); setForm(null);
      refetch();
    } catch (e) { toast.error(apiError(e, "Reset failed")); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(null); } }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="Manage editions" data-testid={`editions-button-${template.id}`} className="h-7 w-7 p-0 text-white/60 hover:text-white"><Layers className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em]">EDITIONS — {template.title}</DialogTitle>
        </DialogHeader>
        {!editing ? (
          isLoading ? (
            <div className="py-8 text-center text-white/40">Loading editions…</div>
          ) : (
            <>
              <div className="flex items-center gap-4 text-[11px] font-mono uppercase tracking-[0.2em] text-white/50 mb-1">
                <span>{data?.minted_count || 0} minted</span>
                <span>·</span>
                <span>{(data?.edition_count || 0) - (data?.minted_count || 0)} available to design</span>
                {data?.truncated && <span className="text-amber-300">· showing first 600</span>}
              </div>
              <div className="text-[11px] text-white/40 mb-3">Design any edition ahead of time — that art is applied automatically when it's minted. Already-minted editions can still be changed.</div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {editions.map((s) => {
                  const img = slotImage(s);
                  const custom = s.status === "minted" || s.has_override;
                  return (
                    <button key={s.edition_number} onClick={() => startEdit(s)} data-testid={`edition-slot-${s.edition_number}`} className="hud-pill rounded-lg overflow-hidden text-left hover:border-cyan-400/40 transition-colors duration-150">
                      <div className={`relative ${s.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} bg-black`}>
                        {img && <img src={img} alt="" className="absolute inset-0 w-full h-full" style={cardImageStyle(s)} />}
                        <div className="absolute top-1 left-1 font-mono text-[10px] bg-black/60 px-1.5 py-0.5 rounded text-white">#{s.edition_number}</div>
                        <div className={`absolute bottom-1 right-1 font-mono text-[8px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${s.status === "minted" ? "bg-emerald-400/90 text-black" : s.has_override ? "bg-cyan-400/90 text-black" : "bg-white/15 text-white/70"}`}>
                          {s.status === "minted" ? "Minted" : s.has_override ? "Designed" : "Default"}
                        </div>
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-white/70 truncate">{s.status === "minted" ? s.owner_name : "Unminted"}</div>
                        <div className="text-[10px] text-cyan-300 inline-flex items-center gap-1 mt-0.5"><Pencil className="h-3 w-3" /> {s.status === "minted" ? "Edit" : "Design"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )
        ) : (
          <div className="space-y-3">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(null); setForm(null); }} className="text-white/60 -ml-2">&larr; Back to editions</Button>
            <div className="font-mono text-xs text-white/50">
              {editing.status === "minted"
                ? <>Editing minted Edition #{editing.edition_number} &middot; Owner: {editing.owner_name}</>
                : <>Pre-designing Edition #{editing.edition_number} (not yet minted)</>}
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Title (this edition)</Label>
              <Input data-testid="edition-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Edition Date (shown on this card, overrides mint date)</Label>
              <Input data-testid="edition-date-input" type="date" value={form.edition_date} onChange={(e) => setForm((f) => ({ ...f, edition_date: e.target.value }))} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
            <ImageUpload value={form.front_image} onChange={(v) => setForm((f) => ({ ...f, front_image: v }))} label="Front Image (this edition only)" testid="edition-front-upload" />
            <CardFramingEditor form={form} setForm={setForm} />
            <div className="flex gap-2">
              <Button data-testid="edition-save-button" onClick={save} className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300">{editing.status === "minted" ? "Save Edition" : "Save Design"}</Button>
              {editing.status === "unminted" && editing.has_override && (
                <Button data-testid="edition-clear-button" variant="outline" onClick={clearDesign} className="border-white/15 text-white/70">Reset to default</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function RecallDialog({ template }) {
  const [open, setOpen] = useState(false);
  const [refund, setRefund] = useState(template.list_price ?? 0);
  const qc = useQueryClient();
  const minted = template.minted_count || 0;
  const total = (Number(refund) || 0) * minted;
  const submit = async () => {
    try {
      const r = await api.post(`/admin/templates/${template.id}/recall`, { refund_per_card: Number(refund) || 0 });
      toast.success(`Recalled: removed ${r.data.cards_removed} card(s), refunded ${r.data.total_refunded.toLocaleString()} credits to ${r.data.users_affected} user(s)`);
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      setOpen(false);
    } catch (e) { toast.error(apiError(e, "Recall failed")); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setRefund(template.list_price ?? 0); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`recall-button-${template.id}`} className="h-7 w-7 p-0 text-red-400 hover:text-red-300" title="Recall & remove from all owners"><Ban className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em] text-red-300">RECALL — {template.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-red-400/30 bg-red-400/10 p-3 flex gap-2 text-sm text-red-200/90">
            <AlertTriangle className="h-5 w-5 shrink-0 text-red-300" />
            <span>This permanently removes <b>every edition</b> of this card from all owners and deletes the template & store listings. Owners are refunded the amount below. This cannot be undone.</span>
          </div>
          <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2 text-sm">
            <span className="font-mono uppercase tracking-[0.2em] text-white/50">Editions in circulation</span>
            <span className="font-mono text-white" data-testid="recall-minted-count">{minted}</span>
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Refund per card (credits)</Label>
            <Input data-testid="recall-refund-input" type="number" min={0} value={refund} onChange={(e) => setRefund(e.target.value)} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            <div className="text-[11px] text-white/40 mt-1">{template.list_price != null ? `Prefilled from store price (${template.list_price.toLocaleString()} credits).` : "No store listing — defaults to 0. Set a refund if desired."}</div>
          </div>
          <div className="flex items-center justify-between hud-pill rounded-lg px-3 py-2">
            <span className="font-mono uppercase tracking-[0.2em] text-white/50 text-xs">Total refund</span>
            <span className="font-mono text-amber-300 flex items-center gap-1.5" data-testid="recall-total-refund"><Coins className="h-4 w-4" /> {total.toLocaleString()}</span>
          </div>
          <Button data-testid="recall-confirm-button" onClick={submit} className="w-full bg-red-500 text-white hover:bg-red-400">
            <Ban className="h-4 w-4 mr-1.5" /> Recall &amp; Refund {minted} Owner{minted === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BulkUploadDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [settings, setSettings] = useState({ rarity: "common", edition_count: 100, orientation: "portrait", image_fit: "cover", image_scale: 1, image_pos_x: 50, image_pos_y: 50, tags: "" });
  const fileRef = useRef();

  // Ask the vision model to name + describe one card from its artwork.
  const describeItem = async (idx, item, rarity) => {
    setItems((prev) => prev.map((p) => p.clientId === item.clientId ? { ...p, ai: "pending" } : p));
    try {
      const r = await api.post("/admin/templates/ai-describe", { image_base64: item.front_image, rarity });
      setItems((prev) => prev.map((p) => p.clientId === item.clientId ? {
        ...p, ai: r.data.fallback ? "fallback" : "done",
        title: r.data.title || p.title,
        description: r.data.description || "",
        lore: r.data.lore || "",
        tags: r.data.tags || [],
      } : p));
      return true;
    } catch (e) {
      setItems((prev) => prev.map((p) => p.clientId === item.clientId ? { ...p, ai: "error" } : p));
      return false;
    }
  };

  // Run AI naming across every item that hasn't been done yet — in parallel with a
  // bounded worker pool so a bulk drop of N images takes ~N/POOL the time, not N×.
  const describeAll = async (list, rarity, concurrency = 6) => {
    setAiBusy(true);
    let failures = 0;
    const queue = list.map((it, i) => ({ it, i })).filter(({ it }) => it.ai !== "done");
    let cursor = 0;
    const worker = async () => {
      while (cursor < queue.length) {
        const { it, i } = queue[cursor++];
        const ok = await describeItem(i, it, rarity);
        if (!ok) failures++;
      }
    };
    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
    setAiBusy(false);
    if (failures) toast.error(`AI naming failed for ${failures} card(s) — use the ↻ button to retry.`);
    else toast.success("AI named & described every card.");
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const next = [];
    const failed = [];
    for (const f of files) {
      try {
        if (f.size > 25 * 1024 * 1024) { failed.push(f.name); continue; }
        const dataUrl = await compressImage(f, { maxSize: 2400, quality: 0.92 });
        next.push({
          clientId: crypto.randomUUID(),
          title: f.name.replace(/\.[^.]+$/, "").slice(0, 60),
          front_image: dataUrl,
          description: "",
          lore: "",
          tags: [],
        });
      } catch {
        failed.push(f.name);
      }
    }
    const merged = [...items, ...next];
    setItems(merged);
    setBusy(false);
    if (failed.length) toast.error(`Couldn't read ${failed.length} file(s): ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}. Try PNG/JPG/WEBP.`);
    // Auto-name freshly added cards with the vision model.
    if (next.length) describeAll(merged, settings.rarity);
  };

  const setItemField = (idx, field, value) =>
    setItems((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  const submit = async () => {
    if (!items.length) { toast.error("No images uploaded"); return; }
    const batchTags = (settings.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
    const payloadItems = items.map((it) => ({
      title: it.title,
      front_image: it.front_image,
      description: it.description || "",
      lore: it.lore || "",
      rarity: settings.rarity,
      edition_count: Number(settings.edition_count) || 1,
      tags: Array.from(new Set([...(it.tags || []), ...batchTags])),
      orientation: settings.orientation,
      image_fit: settings.image_fit,
      image_scale: settings.image_scale,
      image_pos_x: settings.image_pos_x,
      image_pos_y: settings.image_pos_y,
    }));
    try {
      setBusy(true);
      const r = await api.post("/admin/templates/bulk", { items: payloadItems });
      toast.success(`Created ${r.data.count} template${r.data.count === 1 ? "" : "s"}`, {
        description: r.data.skipped ? `${r.data.skipped} skipped — identical artwork already exists.` : undefined,
      });
      setItems([]);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, "Bulk create failed"));
    } finally { setBusy(false); }
  };

  const previewForm = { ...settings, front_image: items[0]?.front_image };
  const thumbAspect = settings.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-bulk-upload-button" variant="outline" className="border-white/15 text-white"><FolderUp className="h-4 w-4 mr-1" /> Bulk Upload</Button>
      </DialogTrigger>
      <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-[0.08em]">BULK UPLOAD TEMPLATES</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Rarity</Label>
              <Select value={settings.rarity} onValueChange={(v) => setSettings((s) => ({ ...s, rarity: v }))}>
                <SelectTrigger data-testid="bulk-rarity-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                  {RARITIES.map((r) => <SelectItem key={r} value={r} className="uppercase tracking-[0.2em]">{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Edition Count</Label>
              <Input data-testid="bulk-edition-input" type="number" min={1} value={settings.edition_count} onChange={(e) => setSettings((s) => ({ ...s, edition_count: Number(e.target.value) }))} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
            </div>
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Extra tags (added to every card, comma-separated)</Label>
            <Input data-testid="bulk-tags-input" value={settings.tags} onChange={(e) => setSettings((s) => ({ ...s, tags: e.target.value }))} placeholder="dragon, fire, mythical" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          {/* Same framing generation as the New Template flow — applied to all uploads */}
          <CardFramingEditor form={previewForm} setForm={setSettings} />
          <div className="text-[11px] text-white/40">Rarity, edition count, framing and extra tags above apply to <span className="text-white/70">every</span> image. The card <span className="text-cyan-300">name, description &amp; lore are written by AI</span> from each artwork.</div>
          <input ref={fileRef} data-testid="bulk-file-input" type="file" accept="image/*" multiple onChange={onFiles} className="hidden" />
          <div className="flex gap-2">
            <Button type="button" onClick={() => fileRef.current.click()} disabled={busy || aiBusy} variant="outline" className="flex-1 border-white/15 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {busy ? "Compressing…" : "Select Images"}
            </Button>
            {items.length > 0 && (
              <Button type="button" data-testid="bulk-ai-rename-all" onClick={() => describeAll(items.map((it) => ({ ...it, ai: undefined })), settings.rarity)} disabled={aiBusy || busy} className="bg-violet-500 text-white hover:bg-violet-400">
                {aiBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                {aiBusy ? "Writing…" : "Re-run AI"}
              </Button>
            )}
          </div>
          {items.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="bulk-items-list">
              {items.map((it, i) => (
                <div key={it.clientId} className="hud-pill rounded-lg p-2 flex gap-2.5" data-testid={`bulk-item-${i}`}>
                  <div className={`relative ${thumbAspect} w-16 shrink-0 bg-black rounded overflow-hidden`}>
                    <img src={it.front_image} alt="" className="absolute inset-0 w-full h-full" style={cardImageStyle(settings)} />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Input data-testid={`bulk-item-title-${i}`} value={it.title} onChange={(e) => setItemField(i, "title", e.target.value)} placeholder="Card name" className="h-7 text-sm bg-[hsl(220_18%_12%)] border-white/10" />
                      {it.ai === "pending" && <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-300 shrink-0" />}
                      {it.ai === "done" && <Sparkles className="h-3.5 w-3.5 text-violet-300 shrink-0" title="AI generated" />}
                      {it.ai === "fallback" && <Sparkles className="h-3.5 w-3.5 text-pink-400 shrink-0" title="Image couldn't be auto-described — gave it a quirky name. Add description & lore manually." />}
                      <button type="button" data-testid={`bulk-item-reroll-${i}`} onClick={() => describeItem(i, it, settings.rarity)} disabled={it.ai === "pending"} className="text-white/40 hover:text-violet-300 shrink-0" title="Re-roll AI text">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <Input data-testid={`bulk-item-desc-${i}`} value={it.description || ""} onChange={(e) => setItemField(i, "description", e.target.value)} placeholder={it.ai === "pending" ? "AI is writing…" : "Description"} className="h-7 text-xs bg-[hsl(220_18%_12%)] border-white/10 text-white/80" />
                    <Textarea data-testid={`bulk-item-lore-${i}`} value={it.lore || ""} onChange={(e) => setItemField(i, "lore", e.target.value)} placeholder="Lore" rows={2} className="text-xs bg-[hsl(220_18%_12%)] border-white/10 text-white/70 resize-none min-h-0" />
                    {(it.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {it.tags.map((t) => <span key={t} className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-200 border border-violet-300/20">{t}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button data-testid="bulk-submit-button" onClick={submit} disabled={!items.length || busy || aiBusy} className="w-full bg-cyan-400 text-black hover:bg-cyan-300">
            {busy ? "Creating…" : `Create ${items.length} Template${items.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTemplates() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMap, setSelectedMap] = useState({}); // id -> template (persists across pages)
  const [printOpen, setPrintOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", rarity: "common", front_image: null, back_image: null, edition_count: 100, base_value: "", lore: "", tags: "", orientation: "portrait", image_fit: "cover", image_scale: 1, image_pos_x: 50, image_pos_y: 50 });
  const [aiFilling, setAiFilling] = useState(false);

  const aiFill = async () => {
    if (!form.front_image) { toast.error("Upload a front image first"); return; }
    setAiFilling(true);
    try {
      const r = await api.post("/admin/templates/ai-describe", { image_base64: form.front_image, rarity: form.rarity });
      setForm((f) => ({
        ...f,
        title: r.data.title || f.title,
        description: r.data.description || f.description,
        lore: r.data.lore || f.lore,
        tags: (r.data.tags || []).join(", ") || f.tags,
      }));
      if (r.data.fallback) toast.message("Couldn't auto-describe that image — gave it a quirky name. Add description & lore yourself.");
      else toast.success("AI wrote the card text from the artwork");
    } catch (e) { toast.error(apiError(e, "AI fill failed")); }
    finally { setAiFilling(false); }
  };

  const { data: tplData } = useQuery({
    queryKey: ["admin-templates", search, rarityFilter, page, pageSize],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (search) p.set("q", search);
      if (rarityFilter !== "all") p.set("rarity", rarityFilter);
      p.set("page", page);
      p.set("page_size", pageSize);
      return (await api.get(`/admin/templates?${p}`)).data;
    },
    keepPreviousData: true,
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload = { ...form, tags: (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean) };
      return (await api.post("/admin/templates", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-templates"] }); setOpen(false); setForm({ title: "", description: "", rarity: "common", front_image: null, back_image: null, edition_count: 100, lore: "", tags: "", orientation: "portrait", image_fit: "cover", image_scale: 1, image_pos_x: 50, image_pos_y: 50 }); toast.success("Template created"); },
    onError: (e) => toast.error(apiError(e, "Create failed")),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/templates/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-templates"] }); toast.success("Deleted"); },
    onError: (e) => toast.error(apiError(e, "Delete failed")),
  });

  const { data: featured } = useQuery({
    queryKey: ["home-featured"],
    queryFn: async () => (await api.get("/home/featured")).data,
  });
  const featuredIds = new Set((featured || []).map((c) => c.template_id));

  const selectedList = Object.values(selectedMap);
  const editionCardObj = (t, e) => ({
    id: `${t.id}-${e.edition_number}`,
    front_image: e.front_image,
    orientation: e.orientation,
    image_fit: e.image_fit,
    image_scale: e.image_scale,
    image_pos_x: e.image_pos_x,
    image_pos_y: e.image_pos_y,
    title: e.title,
    rarity: e.rarity,
    code: e.code,
    edition_number: e.edition_number,
    edition_count: e.edition_count,
  });
  // In print mode, clicking a template selects ALL its coded editions (each
  // physical card = one edition with its own claim code).
  const toggleSelect = async (t) => {
    if (t.distribution_mode === "shared_claim") { toast.error("Shared cards are digital-only and cannot be printed."); return; }
    try {
      const { data } = await api.get(`/admin/templates/${t.id}/edition-codes`);
      const coded = (data.editions || []).filter((e) => e.status === "printed");
      if (coded.length === 0) {
        toast.error("No printable editions — generate codes (minted editions can't be printed).");
        return;
      }
      setSelectedMap((m) => {
        const next = { ...m };
        const ids = coded.map((e) => `${t.id}-${e.edition_number}`);
        const anySelected = ids.some((id) => next[id]);
        if (anySelected) {
          ids.forEach((id) => delete next[id]);
        } else {
          coded.forEach((e) => { next[`${t.id}-${e.edition_number}`] = editionCardObj(t, e); });
        }
        return next;
      });
    } catch (e) {
      toast.error(apiError(e, "Couldn't load edition codes"));
    }
  };
  const templateSelectedCount = (t) => Object.keys(selectedMap).filter((k) => k.startsWith(`${t.id}-`)).length;
  const selectAllOnPage = async (list) => {
    for (const t of list) {
      // eslint-disable-next-line no-await-in-loop
      try {
        const { data } = await api.get(`/admin/templates/${t.id}/edition-codes`);
        const coded = (data.editions || []).filter((e) => e.status === "printed");
        if (coded.length) {
          setSelectedMap((m) => {
            const next = { ...m };
            coded.forEach((e) => { next[`${t.id}-${e.edition_number}`] = editionCardObj(t, e); });
            return next;
          });
        }
      } catch { /* skip templates that fail */ }
    }
  };
  const clearSelection = () => setSelectedMap({});

  const setFeatured = useMutation({
    mutationFn: async (template_id) => (await api.post("/admin/home/featured", { template_id })).data,
    onSuccess: (d, vars) => {
      qc.invalidateQueries({ queryKey: ["home-featured"] });
      const n = (d.featured_template_ids || []).length;
      toast.success(vars ? `Home page cards updated (${n}/3 featured)` : "Cleared home page cards");
    },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">CARD TEMPLATES</h1>
        <div className="flex flex-wrap items-center gap-2">
          <PurgeUnmintedDialog />
          <DedupeTemplatesDialog />
          <AutoOrientTemplatesButton />
          <Button
            data-testid="admin-select-mode-toggle"
            variant="outline"
            onClick={() => { setSelectMode((v) => !v); if (selectMode) clearSelection(); }}
            className={cn("border-white/15", selectMode ? "bg-cyan-400/15 border-cyan-300/50 text-cyan-200" : "text-white")}
          >
            <Printer className="h-4 w-4 mr-1" /> {selectMode ? "Cancel Select" : "Print to PDF"}
          </Button>
          {selectMode && (
            <Button
              data-testid="admin-open-print-sheet"
              onClick={() => setPrintOpen(true)}
              disabled={selectedList.length === 0}
              className="bg-cyan-400 text-black hover:bg-cyan-300"
            >
              <Printer className="h-4 w-4 mr-1" /> Make PDF ({selectedList.length})
            </Button>
          )}
          <BulkUploadDialog onDone={() => qc.invalidateQueries({ queryKey: ["admin-templates"] })} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admin-new-template-button" className="bg-cyan-400 text-black hover:bg-cyan-300"><Plus className="h-4 w-4 mr-1" /> New Template</Button>
            </DialogTrigger>
            <DialogContent className="bg-[hsl(222_22%_8%)] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[0.08em]">NEW TEMPLATE</DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                <div className="sm:col-span-2">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Title</Label>
                  <Input data-testid="template-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Description</Label>
                  <Textarea data-testid="template-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Rarity</Label>
                  <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
                    <SelectTrigger data-testid="template-rarity-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                      {RARITIES.map((r) => <SelectItem key={r} value={r} className="uppercase tracking-[0.2em]">{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Edition Count</Label>
                  <Input data-testid="template-edition-input" type="number" min={1} value={form.edition_count} onChange={(e) => setForm({ ...form, edition_count: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <ImageUpload value={form.front_image} onChange={(v) => setForm({ ...form, front_image: v })} label="Front Image (required)" testid="template-front-upload" />
                <ImageUpload value={form.back_image} onChange={(v) => setForm({ ...form, back_image: v })} label="Back Image (optional)" testid="template-back-upload" />
                <CardFramingEditor form={form} setForm={setForm} />
                <Button type="button" data-testid="template-ai-fill" onClick={aiFill} disabled={!form.front_image || aiFilling} variant="outline" className="sm:col-span-2 border-violet-400/40 text-violet-200 hover:bg-violet-400/10">
                  {aiFilling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {aiFilling ? "Reading the artwork…" : "Auto-fill name, description & lore from image"}
                </Button>
                <div className="sm:col-span-2">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Lore (optional)</Label>
                  <Textarea data-testid="template-lore-input" value={form.lore} onChange={(e) => setForm({ ...form, lore: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Tags (comma-separated)</Label>
                  <Input data-testid="template-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="dragon, fire, mythical" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
                <Button data-testid="template-create-button" onClick={() => create.mutate()} disabled={!form.title || !form.front_image || create.isPending} className="sm:col-span-2 bg-cyan-400 text-black hover:bg-cyan-300">
                  {create.isPending ? "Creating…" : "Create Template"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="admin-templates-search" placeholder="Search by title or serial number…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-[hsl(220_18%_12%)] border-white/10" />
        </div>
        <div className="-mx-1 overflow-x-auto lg:overflow-visible">
          <Tabs value={rarityFilter} onValueChange={(v) => { setRarityFilter(v); setPage(0); }}>
            <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex flex-nowrap">
              {["all", ...RARITIES].map((r) => <TabsTrigger key={r} value={r} className="uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">{r}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>
      </div>

      {(() => {
        const list = tplData?.items || [];
        const total = tplData?.total || 0;
        return (
          <>
            {selectMode && (
              <div className="flex flex-wrap items-center gap-2 mb-3 hud-pill rounded-lg px-3 py-2" data-testid="print-select-bar">
                <span className="text-cyan-200 text-xs uppercase tracking-[0.2em] font-mono">{selectedList.length} selected</span>
                <Button size="sm" variant="ghost" data-testid="print-select-all-page" onClick={() => selectAllOnPage(list)} className="text-white/70 hover:text-white h-7">Select page</Button>
                {selectedList.length > 0 && <Button size="sm" variant="ghost" data-testid="print-clear-selection" onClick={clearSelection} className="text-white/60 hover:text-white h-7"><X className="h-3.5 w-3.5 mr-1" /> Clear</Button>}
                <div className="flex-1" />
                <Button size="sm" data-testid="print-select-bar-make" onClick={() => setPrintOpen(true)} disabled={selectedList.length === 0} className="bg-cyan-400 text-black hover:bg-cyan-300 h-7"><Printer className="h-3.5 w-3.5 mr-1" /> Make PDF</Button>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {list.map((t) => {
                const isSel = templateSelectedCount(t) > 0;
                return (
                <div key={t.id} className={cn("hud-pill rounded-xl overflow-hidden relative", isSel && "ring-2 ring-cyan-400")}>
                  {selectMode && t.distribution_mode !== "shared_claim" && (
                    <button
                      type="button"
                      data-testid={`select-template-${t.id}`}
                      onClick={() => toggleSelect(t)}
                      className={cn(
                        "absolute top-1.5 right-1.5 z-20 h-6 w-6 rounded-md border flex items-center justify-center transition-colors",
                        isSel ? "bg-cyan-400 border-cyan-300 text-black" : "bg-black/60 border-white/30 text-white/70 hover:border-white"
                      )}
                      title={isSel ? "Selected" : "Select for PDF"}
                    >
                      {isSel ? <Check className="h-4 w-4" /> : null}
                    </button>
                  )}
                  <CardHoverPreview card={t} side="top">
                    <div
                      className={`rarity-${t.rarity} relative ${t.orientation === "landscape" ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]"} overflow-hidden bg-[hsl(220_18%_8%)] ${selectMode ? "cursor-pointer" : "cursor-help"}`}
                      data-testid={`admin-template-tile-${t.id}`}
                      onClick={selectMode ? () => toggleSelect(t) : undefined}
                    >
                      {t.front_image && <img src={t.front_image} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full" style={cardImageStyle(t)} />}
                      <div className="foil-overlay" />
                      <div className="absolute bottom-1 left-1"><RarityPill rarity={t.rarity} /></div>
                    </div>
                  </CardHoverPreview>
                  <div className="p-2 flex flex-col gap-1">
                    <div className="font-mono text-[10px] text-white truncate" title={t.title}>{t.title}</div>
                    <div data-testid={`template-supply-${t.id}`} className="font-mono text-[9px] text-white/40">{t.distribution_mode === "shared_claim" ? `${t.minted_count} minted · open digital edition` : `${t.minted_count} / ${t.edition_count} minted`}</div>
                    <div className="flex items-center flex-wrap gap-0.5">
                      {t.distribution_mode !== "shared_claim" && <><MintDialog template={t} /><EditionsManagerDialog template={t} /><EditionCodesDialog template={t} /></>}
                      <EditTemplateDialog template={t} />
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`feature-button-${t.id}`}
                        onClick={() => setFeatured.mutate(t.id)}
                        title={featuredIds.has(t.id) ? "Featured on home — click to remove" : "Feature on home page (up to 3)"}
                        className={`h-7 w-7 p-0 ${featuredIds.has(t.id) ? "text-amber-300 hover:text-amber-200" : "text-white/50 hover:text-white"}`}
                      >
                        <Star className="h-4 w-4" fill={featuredIds.has(t.id) ? "currentColor" : "none"} />
                      </Button>
                      {t.distribution_mode !== "shared_claim" && (t.minted_count > 0 ? (
                        <RecallDialog template={t} />
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => { if (window.confirm("Delete template?")) del.mutate(t.id); }} className="h-7 w-7 p-0 text-red-400 hover:text-red-300" title="Delete template"><Trash2 className="h-4 w-4" /></Button>
                      ))}
                    </div>
                  </div>
                </div>
                );
              })}
              {list.length === 0 && <div className="col-span-full text-center text-white/40 py-12">No templates {(search || rarityFilter !== "all") ? "match filters" : "yet"}.</div>}
            </div>
            <PaginationControls
              page={page}
              total={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
              testidPrefix="admin-templates"
            />
          </>
        );
      })()}
      <PrintSheetDialog open={printOpen} onOpenChange={setPrintOpen} cards={selectedList} />
    </div>
  );
}
